import "./App.scss";
import { Routes, Route } from "react-router-dom";
import { AppShell } from "@components/layout";
import Home from "@pages/Home/Home";
import PALConfiguration from "@pages/PALConfiguration";
import { DataSourceRegistration } from "@pages/Administration";
import { useEffect, useMemo } from "react";
import { useGetUserDealersQuery, useGetUserRolesQuery } from "@services/dcmApi";
import { useAppDispatch, useAppSelector } from "@store/store";
import { setSelectedDealer } from "@store/userSlice";
import { logger } from "@/shared/utils/logger";

function App() {
  const dispatch = useAppDispatch();
  const { userName, fullName, selectedDealerCode } = useAppSelector(
    (state) => state.user,
  );

  // Fetch dealers for the logged-in user
  const {
    data: userDealersData,
    isLoading: isLoadingDealers,
    isError: isDealersError,
    error: dealersApiError,
  } = useGetUserDealersQuery({ userName }, { skip: !userName });

  // Log API error for debugging
  useEffect(() => {
    if (isDealersError) {
      logger.error("Failed to fetch user dealers:", dealersApiError);
    }
  }, [isDealersError, dealersApiError]);

  // Extract dealer codes from API response only (no fallback)
  const availableDealers = useMemo(
    () => userDealersData?.dealers?.map((d) => d.dealerCode) ?? [],
    [userDealersData],
  );

  // Get dealer name for the selected dealer code
  const dealerName = useMemo(() => {
    const dealer = userDealersData?.dealers?.find(
      (d) => d.dealerCode === selectedDealerCode,
    );
    return dealer?.dealerName;
  }, [userDealersData, selectedDealerCode]);

  // Compute effective dealer code - use first available if none selected
  const effectiveDealerCode = useMemo(() => {
    if (selectedDealerCode) return selectedDealerCode;
    return availableDealers.length > 0 ? availableDealers[0] : "";
  }, [selectedDealerCode, availableDealers]);

  // Fetch user roles for the effective dealer
  const { data: userRolesData } = useGetUserRolesQuery(
    { userName, dealerCode: effectiveDealerCode },
    { skip: !userName || !effectiveDealerCode },
  );

  // Extract user roles
  const userRoles = useMemo(() => userRolesData?.roles ?? [], [userRolesData]);

  // Auto-select first dealer when available and none selected
  useEffect(() => {
    if (!selectedDealerCode && availableDealers.length > 0) {
      dispatch(setSelectedDealer(availableDealers[0]));
    }
  }, [selectedDealerCode, availableDealers, dispatch]);

  const handleDealerChange = (newDealerCode: string) => {
    dispatch(setSelectedDealer(newDealerCode));
    logger.debug("Dealer changed to:", newDealerCode);
  };

  // Show loading while fetching dealers
  const showLoading = Boolean(userName && isLoadingDealers);

  return (
    <AppShell
      dealerCode={effectiveDealerCode}
      availableDealers={availableDealers}
      onDealerChange={handleDealerChange}
      isLoadingDealers={showLoading}
      userRoles={userRoles}
    >
      <Routes>
        <Route path="/" element={<Home />} />
        <Route
          path="/pal-configuration"
          element={
            <PALConfiguration
              dealerCode={effectiveDealerCode}
              dealerName={dealerName}
              currentUserName={userName}
              currentUserFullName={fullName}
            />
          }
        />
        {/* Administration Routes - Requires CC_ADMIN role */}
        <Route
          path="/administration/data-source-registration"
          element={
            <DataSourceRegistration
              dealerCode={effectiveDealerCode}
              currentUserName={userName}
            />
          }
        />
      </Routes>
    </AppShell>
  );
}

App.displayName = "App";

export default App;
